import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, MenuItem, Snackbar, TextField, ToggleButton,
    ToggleButtonGroup, Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ApiServices from '../../services/ApiServices';
import { isAdmin } from '../Constants';

const GW_BINARY_TYPES = [
    { type: 'ESP_GATEWAY',            label: 'ESP Gateway',        versionKey: 'softwareVersion'    },
    { type: 'CC_CABINET_CONTROLLER',  label: 'Cabinet Controller', versionKey: 'cabinetVersion'     },
    { type: 'COO_ZIGBEE_COORDINATOR', label: 'Zigbee COO',         versionKey: 'coordinatorVersion' },
];

const SKYHUB_BINARY_TYPES = [
    { type: 'COO_ZIGBEE_COORDINATOR', label: 'Zigbee COO',  versionKey: 'coordinatorVersion' },
    { type: 'ESP_GATEWAY',            label: 'ESP Gateway', versionKey: 'softwareVersion'    },
];

const ROW = { display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1 };
const LABEL_SX = { width: 130, flexShrink: 0, pt: 0.5 };

function GwSelfOtaForm({ gatewayId, gateway, coordinatorVersion }) {
    // Admin-only override: force citycabinet 1.0 or 2.0 regardless of detected generation
    const [genOverride, setGenOverride] = useState('auto');
    useEffect(() => { setGenOverride('auto'); }, [gatewayId]);

    // x.y → citycabinet::1.0, x.y.z (or v-prefixed) → citycabinet::2.0, unknown → null (no filter)
    const detectedCitycabinetType = useMemo(() => {
        if (!coordinatorVersion) return null;
        return /^\d+\.\d+$/.test(coordinatorVersion) ? 'citycabinet::1.0' : 'citycabinet::2.0';
    }, [coordinatorVersion]);

    // No cabinetVersion → device is a SkyHub; use skyhub firmware and only 2 binary types.
    // The generation override doesn't apply to SkyHub — it has no 1.0/2.0 distinction.
    const isSkyhub = !gateway?.cabinetVersion?.trim();
    const citycabinetType = genOverride === 'mesh1' ? 'citycabinet::1.0'
        : genOverride === 'mesh2' ? 'citycabinet::2.0'
        : detectedCitycabinetType;
    const firmwareType = isSkyhub ? 'skyhub' : citycabinetType;

    // True when the admin has forced a generation that doesn't match the detected one
    const isCrossGenOverride = !isSkyhub && genOverride !== 'auto'
        && detectedCitycabinetType !== null && citycabinetType !== detectedCitycabinetType;

    // Cabinet Controller is not upgraded during a cross-generation switch
    const activeBinaryTypes = isSkyhub ? SKYHUB_BINARY_TYPES
        : isCrossGenOverride ? GW_BINARY_TYPES.filter(({ type }) => type !== 'CC_CABINET_CONTROLLER')
        : GW_BINARY_TYPES;

    const [firmwareOptions, setFirmwareOptions] = useState({});
    const [loadingFirmware, setLoadingFirmware] = useState(true);
    const [selected, setSelected] = useState(
        () => activeBinaryTypes.reduce((acc, { type }) => ({ ...acc, [type]: '' }), {})
    );
    const [submitting, setSubmitting] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);

    const showSnack = (message, severity = 'error') =>
        setSnack({ open: true, severity, message });

    useEffect(() => {
        setLoadingFirmware(true);
        setSelected(activeBinaryTypes.reduce((acc, { type }) => ({ ...acc, [type]: '' }), {}));
        const fetches = activeBinaryTypes.map(({ type }) =>
            ApiServices.searchFirmwareByMcuType(type, firmwareType)
                .then(res => ({ type, list: (res.status === 204 || !res.data) ? [] : res.data }))
                .catch(() => ({ type, list: [] }))
        );
        Promise.all(fetches).then(results => {
            const opts = {};
            results.forEach(({ type, list }) => { opts[type] = list; });
            setFirmwareOptions(opts);
        }).finally(() => setLoadingFirmware(false));
    }, [firmwareType, isSkyhub, isCrossGenOverride]);

    const resetForm = () => {
        setSelected(activeBinaryTypes.reduce((acc, { type }) => ({ ...acc, [type]: '' }), {}));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const binaryEntries = activeBinaryTypes
            .filter(({ type }) => selected[type] !== '')
            .map(({ type }) => {
                const fw = (firmwareOptions[type] ?? []).find(f => f.version === selected[type]);
                return fw ? { type, version: fw.version, firmware_type: firmwareType ?? fw.type } : null;
            })
            .filter(Boolean);

        if (binaryEntries.length === 0) {
            showSnack('Select at least one firmware version.');
            return;
        }

        const payload = {
            gateway_id: gatewayId,
            binary: binaryEntries,
        };

        if (isCrossGenOverride) {
            setPendingPayload(payload);
            setConfirmOpen(true);
            return;
        }

        doSubmit(payload);
    };

    const doSubmit = (payload) => {
        setSubmitting(true);
        ApiServices.startGwSelfOta(payload)
            .then(res => {
                showSnack(`GW OTA started — session: ${res.data?.otaId ?? 'OK'}`, 'success');
                resetForm();
            })
            .catch(err => showSnack(err.response?.data ?? err.message))
            .finally(() => setSubmitting(false));
    };

    const handleConfirmCrossGen = () => {
        setConfirmOpen(false);
        doSubmit(pendingPayload);
        setPendingPayload(null);
    };

    if (loadingFirmware) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={4000}
                open={snack.open}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
            >
                <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>

            {isAdmin() && !isSkyhub && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Generation override (admin)
                    </Typography>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={genOverride}
                        onChange={(e, val) => val && setGenOverride(val)}
                    >
                        <ToggleButton value="auto">Auto</ToggleButton>
                        <ToggleButton value="mesh1">Force 1.0</ToggleButton>
                        <ToggleButton value="mesh2">Force 2.0</ToggleButton>
                    </ToggleButtonGroup>
                    {isCrossGenOverride && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            Detected generation is {detectedCitycabinetType === 'citycabinet::1.0' ? '1.0' : '2.0'}.
                            Switching to {citycabinetType === 'citycabinet::1.0' ? '1.0' : '2.0'} firmware should
                            only be performed by an admin.
                        </Alert>
                    )}
                </Box>
            )}

            {activeBinaryTypes.map(({ type, label, versionKey }) => {
                const options = firmwareOptions[type] ?? [];
                const currentVersion = gateway?.[versionKey];
                return (
                    <Box key={type} sx={ROW}>
                        <Box sx={LABEL_SX}>
                            <Typography variant="body2" sx={{ fontSize: 13 }}>{label}</Typography>
                            {currentVersion && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Current: {currentVersion}
                                </Typography>
                            )}
                        </Box>
                        <TextField
                            select
                            size="small"
                            sx={{ flex: 1 }}
                            value={selected[type] ?? ''}
                            onChange={e => setSelected(s => ({ ...s, [type]: e.target.value }))}
                            disabled={options.length === 0}
                            helperText={options.length === 0 ? 'No firmware uploaded' : ''}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {options.map(fw => (
                                <MenuItem key={fw.version} value={fw.version}>
                                    {fw.version}{!fw.released ? ' (Not Released)' : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button variant="contained" size="small" type="submit" disabled={submitting}>
                    {submitting ? 'Starting...' : 'Start OTA'}
                </Button>
            </Box>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm cross-generation flash</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to flash {citycabinetType === 'citycabinet::1.0' ? '1.0' : '2.0'} firmware
                        onto a gateway detected as {detectedCitycabinetType === 'citycabinet::1.0' ? '1.0' : '2.0'}.
                        This operation should only be performed by an admin. Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmCrossGen} color="error" variant="contained">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default GwSelfOtaForm;
